import React, { useContext, useEffect, useState } from 'react';
import CompactNotes from './CompactNotes';
import NextSteps from './NextSteps/NextSteps';
import { Box, Card, Tabs, Tab, Tooltip } from '@mui/material';
import { Error } from '@mui/icons-material';
import { UserContext } from 'op-contexts';
import { useQuery } from '@apollo/client';
import { GET_LATEST_DRAFT_NEXT_STEP } from './NextSteps/Queries';
import { useRouteMatch } from 'react-router-dom';

interface NextStepsNotesContainerProps {
  patientId: string;
}

const UnsubmittedChangesTooltip = () => (
  <Tooltip
    title="Unsubmitted changes"
    arrow
    placement="top"
    componentsProps={{
      tooltip: {
        sx: {
          bgcolor: 'rgba(57, 75, 104, 0.90)',
          fontSize: '0.9rem',
          textAlign: 'center',
          borderRadius: '4px',
          padding: '8px',
        },
      },
      arrow: { sx: { color: 'rgba(57, 75, 104, 0.90)' } },
    }}>
    <Box sx={{ display: 'flex', alignItems: 'center', paddingRight: '4px', height: '20px' }}>
      <Error sx={{ width: '20px', height: '20px', color: 'warning.dark' }} />
    </Box>
  </Tooltip>
);

const NextStepsNotesContainer: React.FC<NextStepsNotesContainerProps> = ({ patientId }) => {
  const match = useRouteMatch();
  const { tab } = match.params as { tab: string };
  const [activeTab, setActiveTab] = useState<number>(tab === 'nextSteps' ? 1 : 0);
  const [showWarningIcon, setShowWarningIcon] = useState(false);
  const { hasFeature } = useContext(UserContext);

  const showNextSteps = hasFeature('Next Step');

  const { data: latestDraftNextStep, refetch: refetchLatestDraft } = useQuery(GET_LATEST_DRAFT_NEXT_STEP, {
    variables: {
      patientId: patientId,
    },
    skip: !showNextSteps, // Only fetch if Next Steps feature is enabled
    fetchPolicy: 'cache-and-network', // Ensures fresh data on initial render
    notifyOnNetworkStatusChange: true, // Notifies on loading state changes
  });

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);

    // Refetch draft data when switching to Notes tab to ensure warning icon is accurate
    if (newValue === 0 && refetchLatestDraft) {
      refetchLatestDraft();
    }
  };

  // Update warning icon based on draft status and data
  useEffect(() => {
    if (latestDraftNextStep?.latestDraftNextStep?.status === 'DRAFT') {
      const draft = latestDraftNextStep.latestDraftNextStep;

      // Check if any fields have data
      const hasData =
        draft.nextStep ||
        draft.nextAppointmentType ||
        draft.nextAppointmentMethod ||
        draft.timingUnit ||
        draft.timingValue ||
        draft.comments;

      // Show warning icon when there's incomplete data
      setShowWarningIcon(!!hasData);
    } else {
      setShowWarningIcon(false);
    }
  }, [latestDraftNextStep]);

  // If Next Steps is disabled and user is on that tab, switch to Notes tab
  useEffect(() => {
    if (!showNextSteps && activeTab === 1) {
      setActiveTab(0);
    }
  }, [showNextSteps, activeTab]);

  return (
    <>
      {showNextSteps ? (
        <Card style={{ height: '100%' }}>
          <Tabs value={activeTab} onChange={handleTabChange} style={{ marginLeft: '16px' }}>
            <Tab label="Notes" />
            {showWarningIcon ? (
              <Tab
                label="Next Step"
                icon={<UnsubmittedChangesTooltip />}
                iconPosition="start"
                sx={{ minHeight: '48px' }}
              />
            ) : (
              <Tab label="Next Step" sx={{ minHeight: '48px' }} />
            )}
          </Tabs>
          <Box sx={{ marginTop: '16px', height: '100%' }}>
            {activeTab === 0 ? (
              <CompactNotes />
            ) : (
              <NextSteps patientId={patientId} toggleShowWarningIcon={setShowWarningIcon} />
            )}
          </Box>
        </Card>
      ) : (
        <Card style={{ height: '100%', paddingTop: '16px' }}>
          <CompactNotes />
        </Card>
      )}
    </>
  );
};

export default NextStepsNotesContainer;
